"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useQuickOrderForm = void 0;
var AddToBasket = _interopRequireWildcard(require("@ori-events/add-to-basket"));
var AddToBasketFailed = _interopRequireWildcard(require("@ori-events/add-to-basket-failed"));
var BasketUpdated = _interopRequireWildcard(require("@ori-events/basket-updated"));
var LoyaltyDialogResult = _interopRequireWildcard(require("@ori-events/loyalty-dialog-result"));
var _analyticsData = require("@ori/analytics-data");
var _checkoutUtils = require("@ori/checkout-utils");
var _i18n = require("@ori/i18n");
var _downshift = _interopRequireDefault(require("downshift"));
var _lodash = _interopRequireDefault(require("lodash.debounce"));
var _react = require("react");
var _api = require("../api");
var _contexts = require("../contexts");
var _logger = require("../logger");
function _getRequireWildcardCache(e) {
  if ("function" != typeof WeakMap) return null;
  var r = new WeakMap(),
    t = new WeakMap();
  return (_getRequireWildcardCache = function (e) {
    return e ? t : r;
  })(e);
}
function _interopRequireWildcard(e, r) {
  if (!r && e && e.__esModule) return e;
  if (null === e || "object" != typeof e && "function" != typeof e) return {
    default: e
  };
  var t = _getRequireWildcardCache(r);
  if (t && t.has(e)) return t.get(e);
  var n = {
      __proto__: null
    },
    a = Object.defineProperty && Object.getOwnPropertyDescriptor;
  for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) {
    var i = a ? Object.getOwnPropertyDescriptor(e, u) : null;
    i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u];
  }
  return n.default = e, t && t.set(e, n), n;
}
const DEBOUNCE_TIMEOUT = 350;
const INVALID_PRODUCT_CODE_CHARACTERS = ['E', 'e', '.', '+', '-'];
const USER_CANCEL_MESSAGE = 'User closed the loyalty dialog.';
const hookName = 'useQuickOrderForm';
const addToBasket = async ({
  itemCode,
  name,
  quantity,
  price
}) => new Promise((resolve, reject) => {
  function handleResolve() {
    window.removeEventListener(BasketUpdated.Name, handleResolve);
    window.removeEventListener(AddToBasketFailed.Name, handleReject);
    window.removeEventListener(LoyaltyDialogResult.Name, handleLoyaltyDialogSelection);
    (0, _logger.logInfo)(`${hookName}: addToBasket action success.`);
    resolve();
  }
  function handleReject(e) {
    window.removeEventListener(AddToBasketFailed.Name, handleReject);
    window.removeEventListener(BasketUpdated.Name, handleResolve);
    window.removeEventListener(LoyaltyDialogResult.Name, handleLoyaltyDialogSelection);
    reject(e.detail.localizedMessage);
  }
  function handleLoyaltyDialogSelection(e) {
    window.removeEventListener(LoyaltyDialogResult.Name, handleLoyaltyDialogSelection);
    const {
      result
    } = e.detail;
    if (LoyaltyDialogResult.isLoyaltyCloseResult(result)) {
      reject(new Error(USER_CANCEL_MESSAGE));
    } else {
      resolve();
    }
  }
  window.addEventListener(LoyaltyDialogResult.Name, handleLoyaltyDialogSelection);
  window.addEventListener(BasketUpdated.Name, handleResolve);
  window.addEventListener(AddToBasketFailed.Name, handleReject);
  const [itemName] = name.split(' - ');
  const event = new CustomEvent(AddToBasket.Name, {
    detail: {
      itemCode,
      quantity: Number.parseInt(quantity, 10),
      localName: itemName ?? '',
      globalName: '',
      placement: _analyticsData.Placement.QuickOrderForm,
      price
    }
  });
  (0, _logger.logDebug)(`${hookName}: addToBasket action called. Triggering event:`, event);
  window.dispatchEvent(event);
});
const useQuickOrderForm = () => {
  const {
    language
  } = (0, _i18n.useLanguage)();
  const {
    maxValue,
    minValue,
    defaultQty
  } = (0, _contexts.useCheckoutQuickOrderForm)();
  const [value, setValue] = (0, _react.useState)('');
  const [quantity, setQuantity] = (0, _react.useState)(String(defaultQty));
  const [isFocused, setIsFocused] = (0, _react.useState)(false);
  const [isLoading, setIsLoading] = (0, _react.useState)(false);
  const [error, setError] = (0, _react.useState)('');
  let results = [];
  let price = 0;
  let isWhispererLoading = false;
  let whispererError = '';
  const {
    foundProducts,
    isProductSearchLoading,
    productSearchQueryError
  } = (0, _api.useProductSearchQuery)(value);
  if (productSearchQueryError instanceof Error) {
    whispererError = productSearchQueryError.message;
  }
  if (value) {
    isWhispererLoading = isProductSearchLoading && !whispererError;
  }
  if (foundProducts.length > 0) {
    results = foundProducts;
    price = foundProducts[0]?.price ?? 0;
  }
  const onStateChange = (0, _lodash.default)(({
    inputValue
  }) => {
    if (typeof inputValue === 'string') {
      setValue(inputValue);
    }
  }, DEBOUNCE_TIMEOUT);
  const input = (0, _react.useRef)(null);
  const isFormFocused = ({
    currentTarget,
    relatedTarget,
    type
  }) => {
    const formElement = input.current?.parentElement?.parentElement;
    const element = type === 'blur' ? relatedTarget : currentTarget;
    return Boolean(formElement?.contains(element));
  };
  const normalizeQuantityValue = (0, _react.useCallback)(() => {
    if (!Number.parseInt(quantity, 10) || quantity === '') {
      setQuantity(String(defaultQty));
    }
  }, [quantity, defaultQty]);
  const handleFocus = (0, _react.useCallback)(event => {
    const hasFocus = isFormFocused(event);
    (0, _logger.logDebug)('handleFocus - hasFocus:', hasFocus, '\n\t', event);
    setIsFocused(hasFocus);
  }, []);
  const handleQuantityBlur = (0, _react.useCallback)(event => {
    normalizeQuantityValue();
    const hasFocus = isFormFocused(event);
    (0, _logger.logDebug)('handleQuantityBlur - hasFocus:', hasFocus, '\n\t', event);
    setIsFocused(hasFocus);
  }, [normalizeQuantityValue]);
  const setFocus = (0, _react.useCallback)(() => {
    input.current?.focus();
    setIsFocused(true);
  }, []);
  const handleAddToBasket = (0, _react.useCallback)(async ({
    clearSelection,
    itemCode,
    name,
    price: newPrice,
    quantity: newQuantity
  }) => {
    setIsLoading(true);
    try {
      await addToBasket({
        itemCode,
        name,
        quantity: newQuantity,
        price: newPrice
      });
      clearSelection(() => {
        setValue('');
        setQuantity('1');
      });
    } catch (currentError) {
      if (currentError instanceof Error) {
        if (currentError.message !== USER_CANCEL_MESSAGE) {
          (0, _checkoutUtils.showErrorNotification)();
          (0, _logger.logDebug)('error notification event should be triggered');
          _logger.mainLogger.error(currentError, {
            action: 'handleAddToBasket'
          });
        }
      } else {
        (0, _logger.logDebug)('error is not instance of error');
        const newError = new Error(String(currentError));
        setError(String(currentError));
        _logger.mainLogger.error(newError, {
          action: 'handleAddToBasket'
        });
      }
    }
    setIsLoading(false);
    setFocus();
  }, [setFocus]);
  const handleButtonClick = (0, _react.useCallback)(clearSelection => {
    normalizeQuantityValue();
    void handleAddToBasket({
      clearSelection,
      itemCode: value,
      name: '',
      quantity: quantity || String(defaultQty),
      price
    });
  }, [normalizeQuantityValue, handleAddToBasket, value, quantity, defaultQty, price]);
  const resetError = (0, _react.useCallback)(() => {
    setError('');
  }, []);
  const handleKeyDown = (0, _react.useCallback)(event => {
    const {
      key: pressedKey
    } = event;
    if (INVALID_PRODUCT_CODE_CHARACTERS.some(invalidCharacter => pressedKey === invalidCharacter)) {
      event.preventDefault();
    }
  }, []);
  const handleChange = (0, _react.useCallback)((item, {
    clearSelection
  }) => {
    (0, _logger.logDebug)(`${hookName}: handleChange called. Item:`, item);
    if (typeof item?.value !== 'undefined') {
      const {
        value: itemValue,
        name,
        price: newPrice
      } = item;
      const itemCode = String(itemValue);
      void handleAddToBasket({
        itemCode,
        name,
        quantity,
        clearSelection,
        price: newPrice
      });
    }
  }, [handleAddToBasket, quantity]);
  const stateReducer = (0, _react.useCallback)((state, changes) => {
    (0, _logger.logDebug)(`${hookName}: stateReducer called. State:`, state, 'changes:', changes);
    let parsedValue;
    switch (changes.type) {
      case _downshift.default.stateChangeTypes.blurInput:
      case _downshift.default.stateChangeTypes.mouseUp:
      case _downshift.default.stateChangeTypes.touchEnd:
        return {
          ...changes,
          inputValue: state.inputValue
        };
      case _downshift.default.stateChangeTypes.changeInput:
        parsedValue = Number.parseInt(changes.inputValue ?? '', 10);
        resetError();
        return {
          ...changes,
          inputValue: parsedValue || changes.inputValue === '' ? parsedValue.toString() || '' : state.inputValue
        };
      default:
        return changes;
    }
  }, [resetError]);
  const itemToString = (0, _react.useCallback)(item => item ? item.value.toString() : '', []);
  return {
    defaultQty,
    error,
    handleButtonClick,
    handleChange,
    handleFocus,
    handleKeyDown,
    handleQuantityBlur,
    input,
    isFocused,
    isLoading,
    isWhispererLoading,
    itemToString,
    language,
    maxValue,
    minValue,
    onStateChange,
    quantity,
    results,
    setFocus,
    setQuantity,
    stateReducer,
    value,
    whispererError
  };
};
exports.useQuickOrderForm = useQuickOrderForm;